import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { Bell, Send, Link2, Unlink, Loader2, Copy, Check, ExternalLink, RefreshCw } from 'lucide-react'
import { useAuthStore } from '@/stores/auth.store'
import {
  notificationsService,
  type NotificationPreferences,
} from '@/services/notifications.service'

const ENGINES: { key: 'notify_yomi' | 'notify_jifu' | 'notify_makatsu'; label: string; desc: string }[] = [
  { key: 'notify_yomi', label: 'Yomi', desc: 'Segnali trend-following' },
  { key: 'notify_jifu', label: 'Sempai', desc: 'Alert esterni' },
  { key: 'notify_makatsu', label: 'Makatsu', desc: 'Segnali esterni' },
]

const Toggle = ({ checked, onChange, disabled }: { checked: boolean; onChange: () => void; disabled?: boolean }) => (
  <button
    type="button"
    onClick={onChange}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      checked ? 'bg-primary' : 'bg-muted'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
)

export const Notifications = () => {
  const isAdmin = useAuthStore(s => s.isAdmin)
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null)
  const [deepLink, setDeepLink] = useState<string | null>(null)
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    notificationsService
      .getPreferences()
      .then(setPrefs)
      .catch(() => setMsg({ type: 'err', text: 'Impossibile caricare le preferenze.' }))
      .finally(() => setLoading(false))
  }, [])

  const flash = (type: 'ok' | 'err', text: string) => {
    setMsg({ type, text })
    setTimeout(() => setMsg(null), 4000)
  }

  const save = async (update: Partial<NotificationPreferences>) => {
    if (!prefs) return
    const prev = prefs
    setPrefs({ ...prefs, ...update })
    setSaving(true)
    try {
      const fresh = await notificationsService.updatePreferences(update as any)
      setPrefs(fresh)
    } catch {
      setPrefs(prev)
      flash('err', 'Salvataggio non riuscito.')
    } finally {
      setSaving(false)
    }
  }

  const handleLink = async () => {
    setBusy('link')
    setCopied(false)
    try {
      const { deep_link } = await notificationsService.link()
      if (!/^(https:\/\/t\.me\/|tg:\/\/)/.test(deep_link)) {
        throw new Error('invalid link')
      }
      setDeepLink(deep_link)
      try {
        const url = await QRCode.toDataURL(deep_link, { width: 220, margin: 1 })
        setQrUrl(url)
      } catch {
        setQrUrl(null)
      }
      flash('ok', 'Inquadra il QR col telefono (o usa "Copia link") e premi "Avvia" su Telegram.')
    } catch {
      setDeepLink(null)
      setQrUrl(null)
      flash('err', 'Collegamento non disponibile (bot non configurato).')
    } finally {
      setBusy(null)
    }
  }

  const handleCopy = async () => {
    if (!deepLink) return
    try {
      await navigator.clipboard.writeText(deepLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      flash('err', 'Copia non riuscita. Seleziona e copia il link manualmente.')
    }
  }

  const handleUnlink = async () => {
    setBusy('unlink')
    try {
      await notificationsService.unlink()
      const fresh = await notificationsService.getPreferences()
      setPrefs(fresh)
      setDeepLink(null)
      setQrUrl(null)
      flash('ok', 'Telegram scollegato.')
    } catch {
      flash('err', 'Operazione non riuscita.')
    } finally {
      setBusy(null)
    }
  }

  const handleTest = async () => {
    setBusy('test')
    try {
      await notificationsService.test()
      flash('ok', 'Messaggio di prova inviato su Telegram.')
    } catch {
      flash('err', 'Invio non riuscito. Verifica il collegamento.')
    } finally {
      setBusy(null)
    }
  }

  const handleSetupWebhook = async () => {
    setBusy('webhook')
    try {
      const res = await notificationsService.setupWebhook()
      flash('ok', `Webhook ripuntato su: ${res.url}`)
    } catch (e: any) {
      flash('err', e?.response?.data?.detail ?? 'Operazione non riuscita.')
    } finally {
      setBusy(null)
    }
  }

  const refresh = async () => {
    setLoading(true)
    try {
      const fresh = await notificationsService.getPreferences()
      setPrefs(fresh)
      if (fresh.telegram_linked) {
        setDeepLink(null)
        setQrUrl(null)
      }
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    )
  }

  if (!prefs) {
    return <div className="p-6 text-destructive">Errore nel caricamento.</div>
  }

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Bell className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Notifiche</h1>
          <p className="text-sm text-muted-foreground">
            Ricevi un avviso su Telegram quando c'è un nuovo segnale autorizzato.
          </p>
        </div>
      </div>

      {msg && (
        <div
          className={`rounded-lg px-4 py-3 text-sm ${
            msg.type === 'ok'
              ? 'bg-green-500/10 text-green-600 dark:text-green-400'
              : 'bg-destructive/10 text-destructive'
          }`}
        >
          {msg.text}
        </div>
      )}

      {/* Telegram link */}
      <div className="rounded-xl border bg-card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Telegram</h2>
            <p className="text-sm text-muted-foreground">
              {prefs.telegram_linked ? 'Account collegato ✅' : 'Nessun account collegato'}
            </p>
          </div>
          {prefs.telegram_linked ? (
            <button
              onClick={handleUnlink}
              disabled={busy === 'unlink'}
              className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
            >
              {busy === 'unlink' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />}
              Scollega
            </button>
          ) : (
            <button
              onClick={handleLink}
              disabled={busy === 'link' || !prefs.telegram_configured}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {busy === 'link' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
              Collega Telegram
            </button>
          )}
        </div>

        {!prefs.telegram_configured && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Il bot Telegram non è ancora configurato dall'amministratore.
          </p>
        )}

        {!prefs.telegram_linked && deepLink && (
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <p className="text-sm font-medium">Collega dal telefono</p>
            <ol className="text-xs text-muted-foreground list-decimal list-inside space-y-1">
              <li>Inquadra questo QR con la fotocamera del telefono.</li>
              <li>Si apre Telegram sul bot: premi <b>Avvia</b>.</li>
              <li>Torna qui e premi <b>“Ho premuto Avvia”</b>.</li>
            </ol>
            {qrUrl && (
              <div className="flex justify-center">
                <img
                  src={qrUrl}
                  alt="QR per collegare Telegram"
                  className="rounded-lg bg-white p-2"
                  width={200}
                  height={200}
                />
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleCopy}
                className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-muted"
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copiato!' : 'Copia link'}
              </button>
              <a
                href={deepLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-muted"
              >
                <ExternalLink className="h-4 w-4" />
                Apri Telegram qui
              </a>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Niente fotocamera? Usa “Copia link” e incollalo nel browser del telefono.
            </p>
          </div>
        )}

        {prefs.telegram_linked && (
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button
              onClick={handleTest}
              disabled={busy === 'test'}
              className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
            >
              {busy === 'test' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Invia prova
            </button>
            <button onClick={refresh} className="text-sm text-muted-foreground underline">
              Aggiorna stato
            </button>
          </div>
        )}
        {!prefs.telegram_linked && (
          <button onClick={refresh} className="text-sm text-muted-foreground underline">
            Ho premuto "Avvia" — aggiorna stato
          </button>
        )}
      </div>

      {/* Master + per-engine toggles */}
      <div className="rounded-xl border bg-card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Attiva notifiche Telegram</h2>
            <p className="text-sm text-muted-foreground">Interruttore generale.</p>
          </div>
          <Toggle
            checked={prefs.notify_telegram}
            disabled={!prefs.telegram_linked || saving}
            onChange={() => save({ notify_telegram: !prefs.notify_telegram })}
          />
        </div>

        <div className="border-t pt-4 space-y-4">
          {ENGINES.map((e) => (
            <div key={e.key} className="flex items-center justify-between">
              <div>
                <div className="font-medium">{e.label}</div>
                <div className="text-xs text-muted-foreground">{e.desc}</div>
              </div>
              <Toggle
                checked={prefs[e.key]}
                disabled={!prefs.notify_telegram || !prefs.telegram_linked || saving}
                onChange={() => save({ [e.key]: !prefs[e.key] } as any)}
              />
            </div>
          ))}
        </div>
      </div>

      {isAdmin && (
        <div className="rounded-xl border bg-card p-5 space-y-3">
          <div>
            <h2 className="font-semibold">Amministrazione bot</h2>
            <p className="text-sm text-muted-foreground">
              Dopo ogni publish, ripunta il webhook del bot sul dominio di produzione
              (necessario per il collegamento degli account, non per l'invio delle notifiche).
            </p>
          </div>
          <button
            onClick={handleSetupWebhook}
            disabled={busy === 'webhook'}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {busy === 'webhook' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Ripunta webhook Telegram
          </button>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        I segnali sono informativi e non costituiscono consulenza finanziaria.
      </p>
    </div>
  )
}

export default Notifications
